import { IUser, Role } from '@/models';
import { hasAnyOfTargetRoles, isTargetRole } from '@/services';

export type MasterIndexSection =
  | 'persons'
  | 'organizations'
  | 'vehicles'
  | 'properties'
  | 'address'
  | 'logs';

export type MasterIndexAccess = {
  canSeeMasterIndex: boolean;
  allowed: MasterIndexSection[];
  defaultSection?: MasterIndexSection;
};

export const ALL_SECTIONS: MasterIndexSection[] = [
  'persons',
  'organizations',
  'vehicles',
  'properties',
  'address',
  'logs',
];

const BASE_SECTIONS: MasterIndexSection[] = [
  'persons',
  'organizations',
  'vehicles',
  'address',
  'logs',
];

const canSeeCfsRelatedRecords = (user: IUser) =>
  isTargetRole(user, Role.DISPATCHER);

const canSeeKaseRelatedRecords = (user: IUser, agencyId?: string) =>
  !!agencyId &&
  hasAnyOfTargetRoles(
    user,
    [
      Role.AGENCY_ADMIN,
      Role.RMS_CASES_FULL_ACCESS,
      Role.RMS_CASES_VIEW,
      Role.KASE_APPROVER,
    ],
    agencyId,
  );

// First Responders get per-section Master Index access through the dedicated
// roles below. Order matches ALL_SECTIONS so the tab order stays canonical.
const FIRST_RESPONDER_SECTION_ROLES: [Role, MasterIndexSection][] = [
  [Role.MASTER_INDEX_PERSONS, 'persons'],
  [Role.MASTER_INDEX_VEHICLES, 'vehicles'],
  [Role.MASTER_INDEX_ADDRESSES, 'address'],
];

const firstResponderSections = (user: IUser): MasterIndexSection[] => {
  if (!isTargetRole(user, Role.FIRST_RESPONDER)) return [];
  return FIRST_RESPONDER_SECTION_ROLES.filter(([role]) =>
    isTargetRole(user, role),
  ).map(([, section]) => section);
};

export function computeMasterIndexAccess(
  user: IUser | null,
  agencyId?: string | null,
): MasterIndexAccess {
  if (!user || !agencyId) {
    return { canSeeMasterIndex: false, allowed: [] };
  }

  const canSeeCfs = canSeeCfsRelatedRecords(user);
  const canSeeKase = canSeeKaseRelatedRecords(user, agencyId);

  if (canSeeCfs || canSeeKase) {
    return {
      canSeeMasterIndex: true,
      allowed: canSeeKase ? ALL_SECTIONS : BASE_SECTIONS,
      defaultSection: 'persons',
    };
  }
  const responderSections = firstResponderSections(user);
  if (responderSections.length > 0) {
    return {
      canSeeMasterIndex: true,
      allowed: responderSections,
      defaultSection: responderSections[0],
    };
  }
  return { canSeeMasterIndex: false, allowed: [] };
}
